import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-99b262aa"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "live-online-statistics-page" }
const _hoisted_2 = { class: "live-online-statistics-page__consumer" }

import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import { DataAnalysis, Download } from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import { useRoute } from 'vue-router'
import { CrudSearch, CrudTable } from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import { useStore } from '@/store'
import type { CrudSelectOption } from '@/types/crud-config'
import type { CrudSort } from '@/types/crud'
import {
  createLiveOnlineStatisticsSearchItems,
  liveOnlineStatisticsTableColumns
} from './live-online-statistics.config'
import {
  exportLiveOnlineStatisticsDetail,
  exportLiveOnlineStatisticsSummary,
  fetchLiveOnlineStatisticsFilterOptions,
  fetchLiveOnlineStatisticsPage
} from './live-online-statistics.service'
import type { LiveOnlineStatisticsFilters } from './live-online-statistics.service'


export default /*#__PURE__*/_defineComponent({
  __name: 'LiveOnlineStatistics',
  setup(__props) {

const route = useRoute()
const store = useStore()
const canList = usePermission('liveOnlineStatistics', 'list')
const canDownload = usePermission('liveOnlineStatistics', 'download')
const canSummary = usePermission('liveOnlineStatistics', 'summary')

function readScalarRouteQuery(
  name: 'channelId' | 'consumerNickName' | 'duration' | 'deptId' | 'empIds'
) {
  const value = route.query[name]
  if (value === undefined) return undefined
  if (typeof value !== 'string')
    throw new Error(`路由参数 ${name} 必须是字符串`)
  return value
}

function readRouteFilters(): Partial<LiveOnlineStatisticsFilters> {
  const accessTime = route.query.accessTime
  if (
    accessTime !== undefined &&
    (!Array.isArray(accessTime) ||
      accessTime.length !== 2 ||
      accessTime.some((value) => typeof value !== 'string'))
  ) {
    throw new Error('路由参数 accessTime 必须包含开始和结束时间')
  }

  const names = [
    'channelId',
    'consumerNickName',
    'duration',
    'deptId',
    'empIds'
  ] as const
  const result = names.reduce<Partial<LiveOnlineStatisticsFilters>>(
    (filters, name) => {
      const value = readScalarRouteQuery(name)
      if (value !== undefined) filters[name] = value
      return filters
    },
    {}
  )
  if (accessTime !== undefined) result.accessTime = accessTime as string[]
  return result
}

let routeFilters: Partial<LiveOnlineStatisticsFilters> = {}
let routeError: Error | null = null
try {
  routeFilters = readRouteFilters()
} catch (cause) {
  routeError = cause instanceof Error ? cause : new Error('路由参数错误')
}

function createFilters(): LiveOnlineStatisticsFilters {
  return {
    channelId: '',
    consumerNickName: '',
    duration: '',
    accessTime: [
      dayjs().startOf('date').format('YYYY-MM-DD HH:mm:ss'),
      dayjs().endOf('date').format('YYYY-MM-DD HH:mm:ss')
    ],
    deptId: '',
    empIds: '',
    ...routeFilters
  }
}

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  lastSuccessfulQuery,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  changeSort,
  refresh
} = usePagedCrud({
  createFilters,
  fetchPage: fetchLiveOnlineStatisticsPage
})

if (routeError) error.value = routeError

const optionsLoading = ref(false)
const detailExporting = ref(false)
const summaryExporting = ref(false)
const channelOptions = ref<CrudSelectOption[]>([])
const employeeOptions = ref<CrudSelectOption[]>([])
const departmentOptions: CrudSelectOption[] = store.state.login.depts.map(
  (item: { id: string | number; name: string }) => ({
    label: item.name,
    value: item.id
  })
)

const searchItems = computed(() =>
  createLiveOnlineStatisticsSearchItems({
    channelOptions: channelOptions.value,
    departmentOptions,
    employeeOptions: employeeOptions.value,
    optionsLoading: optionsLoading.value
  })
)
const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)
const canQuery = computed(() => canList && !routeError)

function runQuery(action: () => Promise<boolean>) {
  return canQuery.value ? action() : Promise.resolve(false)
}

function handleSortChange(value: {
  prop?: string
  order?: 'ascending' | 'descending' | null
}) {
  const sort: CrudSort | undefined =
    value.prop && value.order
      ? { prop: value.prop, order: value.order }
      : undefined
  return runQuery(() => changeSort(sort))
}

function saveBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

async function downloadDetail() {
  if (!lastSuccessfulQuery.value) return
  detailExporting.value = true
  try {
    saveBlob(
      await exportLiveOnlineStatisticsDetail(lastSuccessfulQuery.value),
      '直播间在线客户统计.xlsx'
    )
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '明细导出失败')
  } finally {
    detailExporting.value = false
  }
}

async function downloadSummary() {
  if (!lastSuccessfulQuery.value) return
  summaryExporting.value = true
  try {
    saveBlob(
      await exportLiveOnlineStatisticsSummary(lastSuccessfulQuery.value),
      '直播间在线观看人数汇总.xlsx'
    )
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '汇总导出失败')
  } finally {
    summaryExporting.value = false
  }
}

async function loadFilterOptions() {
  optionsLoading.value = true
  try {
    const options = await fetchLiveOnlineStatisticsFilterOptions()
    channelOptions.value = options.channels.map((item) => ({
      label: item.eventName,
      value: item.rowId
    }))
    employeeOptions.value = options.employees.map((item) => ({
      label: item.realName,
      value: item.id
    }))
  } catch (cause) {
    ElMessage.error(
      cause instanceof Error ? cause.message : '筛选项加载失败，请稍后重试'
    )
  } finally {
    optionsLoading.value = false
  }
}

onMounted(() => {
  loadFilterOptions()
  runQuery(load)
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_avatar = _resolveComponent("el-avatar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: searchItems.value,
      loading: _unref(loading),
      onSearch: _cache[0] || (_cache[0] = ($event: any) => (runQuery(_unref(search)))),
      onReset: _cache[1] || (_cache[1] = ($event: any) => (runQuery(_unref(reset))))
    }, null, 8, ["model", "items", "loading"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(liveOnlineStatisticsTableColumns),
      onPageChange: _cache[2] || (_cache[2] = ($event: any) => (runQuery(() => _unref(changePage)($event)))),
      onPageSizeChange: _cache[3] || (_cache[3] = ($event: any) => (runQuery(() => _unref(changePageSize)($event)))),
      onSortChange: handleSortChange,
      onRetry: _cache[4] || (_cache[4] = ($event: any) => (runQuery(_unref(refresh))))
    }, {
      title: _withCtx(() => [
        _createTextVNode("观看明细")
      ]),
      actions: _withCtx(() => [
        (_unref(canDownload))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              loading: detailExporting.value,
              disabled: summaryExporting.value || !_unref(lastSuccessfulQuery),
              onClick: downloadDetail
            }, {
              default: _withCtx(() => [
                (!detailExporting.value)
                  ? (_openBlock(), _createBlock(_unref(Download), {
                      key: 0,
                      class: "live-online-statistics-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 下载 ")
              ]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true),
        (_unref(canSummary))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 1,
              type: "primary",
              loading: summaryExporting.value,
              disabled: detailExporting.value || !_unref(lastSuccessfulQuery),
              onClick: downloadSummary
            }, {
              default: _withCtx(() => [
                (!summaryExporting.value)
                  ? (_openBlock(), _createBlock(_unref(DataAnalysis), {
                      key: 0,
                      class: "live-online-statistics-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 汇总 ")
              ]),
              _: 1
            }, 8, ["loading", "disabled"]))
          : _createCommentVNode("", true)
      ]),
      consumer: _withCtx(({ row }) => [
        _createElementVNode("div", _hoisted_2, [
          _createVNode(_component_el_avatar, {
            shape: "square",
            size: 60,
            src: row.consumerHeadImgUrl
          }, null, 8, ["src"]),
          _createElementVNode("span", null, _toDisplayString(row.consumerNickName), 1)
        ])
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns"])
  ]))
}
}

})